import { T, useGT, Var } from 'gt-react';

import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Spinner } from 'src/components/ui/spinner';

/**
 * Password logins don't prove who owns the email, so a user confirms
 * their email address with a mailed 6-digit code (once per connection).
 */
export function EmailCodeStep({
  email,
  codeSent,
  isSending,
  onSend,
  code,
  onCodeChange,
}: {
  email: string;
  codeSent: boolean;
  isSending: boolean;
  onSend: () => void;
  code: string;
  onCodeChange: (code: string) => void;
}) {
  const gt = useGT();
  if (!codeSent) {
    return (
      <>
        <p className="text-sm text-gray-500 text-center">
          <T context="Gently MCP consent: explains the email verification step">
            First, confirm it's you: we'll email a code to <Var>{email}</Var>.
          </T>
        </p>
        <Button className="w-full" disabled={isSending} onClick={onSend}>
          {isSending ? (
            <Spinner className="mx-auto text-white" />
          ) : (
            <T context="Gently MCP consent: send the email verification code">
              Send Code
            </T>
          )}
        </Button>
      </>
    );
  }
  return (
    <>
      <p className="text-sm text-gray-500 text-center">
        <T context="Gently MCP consent: a verification code was emailed">
          Enter the 6-digit code we sent to <Var>{email}</Var>. It expires in 10
          minutes.
        </T>
      </p>
      <Input
        value={code}
        onChange={(event) =>
          onCodeChange(event.target.value.replace(/\D/g, '').slice(0, 6))
        }
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        aria-label={gt('Verification code', {
          context: 'Gently MCP consent: label for the emailed code input',
        })}
        className="text-center text-lg tracking-[0.5em]"
      />
      <Button
        variant="link"
        className="self-center"
        disabled={isSending}
        onClick={onSend}
      >
        <T context="Gently MCP consent: send another verification code">
          Resend Code
        </T>
      </Button>
    </>
  );
}
